/**
 * Ornaments, fermatas, arpeggios and tremolo: the signs that ask for more than is written.
 *
 * These are notation and not performance. The document records the sign; it does not turn a
 * trill into the notes a player would make of it, because what those are depends on the
 * century, the instrument and the edition, and a document that guessed would be wrong in a
 * way nobody could correct. The guess is made at the other end instead, in
 * `playback/realize.ts`, where it is one place a caller can switch off -- see `marks` in
 * `playback/played.ts`. Nothing here decides how a sign sounds; it only records that it is there.
 *
 * Like the rest of `marks`, everything here works over a selection and answers with one
 * command, so marking a passage is one undo step.
 */

import type { Arpeggio, FermataShape, MusicEvent, Ornament, Score } from "../document/types";
import type { Command } from "../commands/types";
import { selectedLocations, selectedNotes } from "./selection";
import type { Selection } from "./selection";

/** Every ornament the document knows, in the order a palette should offer them. */
export const ORNAMENTS: readonly Ornament[] = [
  "trill",
  "mordent",
  "inverted-mordent",
  "turn",
  "inverted-turn",
  "delayed-turn",
  "shake",
  "schleifer",
];

/** As many beams as a stem carries. */
export const MAX_TREMOLO = 3;

/**
 * Add an ornament to the selection, or take it off again.
 *
 * A toggle over the whole selection rather than per note, for the reason `articulate` is:
 * toggling each note separately makes a mixed selection alternate stripes.
 */
export function ornament(score: Score, selection: Selection, mark: Ornament): Command | string {
  const found = selectedNotes(score, selection);
  if (found.length === 0) return "no note is selected";
  const everywhere = found.every((at) => at.event.ornaments.includes(mark));
  return {
    kind: "batch",
    label: everywhere ? `no ${mark}` : mark,
    commands: found.map((at) => ({
      kind: "setEventProps" as const,
      eventId: at.event.id,
      props: {
        ornaments: everywhere
          ? at.event.ornaments.filter((held) => held !== mark)
          : [...at.event.ornaments, mark],
      },
    })),
  };
}

/** Take every ornament off the selection. */
export function clearOrnaments(score: Score, selection: Selection): Command | string {
  const found = selectedNotes(score, selection).filter((at) => at.event.ornaments.length > 0);
  if (found.length === 0) return "there are no ornaments to take off";
  return {
    kind: "batch",
    label: "clear ornaments",
    commands: found.map((at) => ({
      kind: "setEventProps" as const,
      eventId: at.event.id,
      props: { ornaments: [] },
    })),
  };
}

/**
 * Hold the selection, or let it go.
 *
 * This is the one mark that reaches rests as well as notes: a held silence is ordinary, and
 * the fermata over the last bar's rest is how a great many pieces end.
 */
export function fermata(
  score: Score,
  selection: Selection,
  shape: FermataShape | null,
): Command | string {
  const found = selectedLocations(score, selection);
  if (found.length === 0) return "nothing is selected";
  return {
    kind: "batch",
    label: shape === null ? "no fermata" : "fermata",
    commands: found.map((at) => ({
      kind: "setEventProps" as const,
      eventId: at.event.id,
      props: { fermata: shape },
    })),
  };
}

/** Put a fermata on the selection if none of it is held, and take it off if any of it is. */
export function toggleFermata(score: Score, selection: Selection): Command | string {
  const found = selectedLocations(score, selection);
  if (found.length === 0) return "nothing is selected";
  const held = found.some((at) => at.event.fermata !== null);
  return fermata(score, selection, held ? null : "normal");
}

/**
 * Spread the selected chords, or stop spreading them.
 *
 * A single note is left alone rather than refused: a passage of chords and single notes is
 * a perfectly ordinary thing to select, and an arpeggio across one note head means nothing,
 * so it is not written for one.
 */
export function arpeggio(
  score: Score,
  selection: Selection,
  style: Arpeggio | null,
): Command | string {
  const found = selectedNotes(score, selection).filter((at) => at.event.pitches.length > 1);
  if (found.length === 0) return "no chord is selected";
  return {
    kind: "batch",
    label: style === null ? "no arpeggio" : `arpeggio ${style}`,
    commands: found.map((at) => ({
      kind: "setEventProps" as const,
      eventId: at.event.id,
      props: { arpeggio: style },
    })),
  };
}

/** Strike `beams` tremolo beams through the selection's stems; `0` takes them off. */
export function tremolo(score: Score, selection: Selection, beams: number): Command | string {
  if (!Number.isInteger(beams) || beams < 0 || beams > MAX_TREMOLO) {
    return `a stem carries between 0 and ${MAX_TREMOLO} tremolo beams`;
  }
  const found = selectedNotes(score, selection);
  if (found.length === 0) return "no note is selected";
  return {
    kind: "batch",
    label: beams === 0 ? "no tremolo" : `tremolo ${beams}`,
    commands: found.map((at) => ({
      kind: "setEventProps" as const,
      eventId: at.event.id,
      props: { tremolo: beams },
    })),
  };
}

/** Whether an event carries any of the marks in this file, for a panel that shows them. */
export function isDecorated(event: MusicEvent): boolean {
  if (event.fermata !== null) return true;
  if (event.kind !== "note") return false;
  return event.ornaments.length > 0 || event.arpeggio !== null || event.tremolo > 0;
}
